import { z } from 'zod';

/**
 * Wave 379BM · Supplier portal público (#94).
 *
 * Token long-lived que dá ao supplier um dashboard read-only:
 *  - cotações abertas em que ele foi convidado
 *  - POs ativas
 *  - pagamentos recentes (últimos 90d)
 *
 * Sem auth — link é o secret. PM gera/regenera via admin endpoint.
 */

export const supplierPortalTokenSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  supplierId: z.string(),
  token: z.string(),
  createdBy: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  lastAccessedAt: z.string().nullable(),
  revokedAt: z.string().nullable(),
});
export type SupplierPortalToken = z.infer<typeof supplierPortalTokenSchema>;

/**
 * Resposta do POST admin · contém token + URL pronta pro PM copiar.
 */
export const generateSupplierPortalTokenResponseSchema = z.object({
  token: z.string(),
  url: z.string().url(),
  isRegeneration: z.boolean(),
});
export type GenerateSupplierPortalTokenResponse = z.infer<
  typeof generateSupplierPortalTokenResponseSchema
>;

/**
 * Status do token portal de um supplier (UI badge "Ativo · acessado há
 * 2d"). Wave 379BY · #129. PM/admin-only — não expõe o token literal,
 * só metadata. Caso o supplier nunca tenha tido token, todos os campos
 * de timestamp ficam null e `active=false`.
 *
 * Semântica:
 *  - `active=true` quando há row com `revoked_at IS NULL` (ativa);
 *    `createdAt`/`lastAccessedAt` referem-se a essa row.
 *  - `active=false` + `revokedAt!=null` quando o token mais recente já
 *    foi revogado (PM regenerou ou revogou manualmente).
 *  - `active=false` + todos os timestamps null quando supplier nunca
 *    teve token.
 */
export const supplierPortalTokenStatusSchema = z.object({
  active: z.boolean(),
  createdAt: z.string().nullable(),
  lastAccessedAt: z.string().nullable(),
  revokedAt: z.string().nullable(),
});
export type SupplierPortalTokenStatus = z.infer<
  typeof supplierPortalTokenStatusSchema
>;

/**
 * Quotation aberta visível ao supplier no portal.
 */
export const supplierPortalOpenQuotationSchema = z.object({
  quotationId: z.string(),
  title: z.string(),
  projectName: z.string().nullable(),
  invitedAt: z.string(),
  dueAt: z.string().nullable(),
  responseStatus: z.enum(['pending', 'submitted', 'rejected', 'won', 'lost']),
  responseToken: z.string().nullable(),
  itemCount: z.number().int().nonnegative().nullable(),
});
export type SupplierPortalOpenQuotation = z.infer<
  typeof supplierPortalOpenQuotationSchema
>;

/**
 * PO ativa visível ao supplier (status != cancelled/closed).
 */
export const supplierPortalActivePoSchema = z.object({
  poId: z.string(),
  poNumber: z.string().nullable(),
  projectName: z.string().nullable(),
  issuedAt: z.string(),
  status: z.string(),
  totalCents: z.number().int().nonnegative(),
  itemCount: z.number().int().nonnegative().nullable(),
});
export type SupplierPortalActivePo = z.infer<typeof supplierPortalActivePoSchema>;

/**
 * Pagamento recente (último 90d) pra esse supplier.
 */
export const supplierPortalPaymentSchema = z.object({
  paymentId: z.string(),
  paidAt: z.string(),
  amountCents: z.number().int().nonnegative(),
  method: z.string().nullable(),
  poNumber: z.string().nullable(),
  projectName: z.string().nullable(),
});
export type SupplierPortalPayment = z.infer<typeof supplierPortalPaymentSchema>;

/**
 * Wave po-supplier-actions · 2026-05-31
 *
 * Detalhe de UM pedido visto pelo fornecedor no portal (GET por token).
 * Inclui as linhas e o estado das 3 ações pós-pedido (aceitar/recusar,
 * nota fiscal, entrega). Sem PII de outros fornecedores.
 */
export const supplierPortalPoLineSchema = z.object({
  description: z.string(),
  quantity: z.number(),
  unit: z.string(),
  unitPriceCents: z.number().int().nonnegative(),
  subtotalCents: z.number().int().nonnegative(),
});
export type SupplierPortalPoLine = z.infer<typeof supplierPortalPoLineSchema>;

export const supplierPortalPoDetailSchema = z.object({
  poId: z.string(),
  poNumber: z.string().nullable(),
  title: z.string(),
  projectName: z.string().nullable(),
  status: z.string(),
  issuedAt: z.string().nullable(),
  expectedDeliveryAt: z.string().nullable(),
  notes: z.string().nullable(),
  items: z.array(supplierPortalPoLineSchema),
  subtotalCents: z.number().int().nonnegative(),
  taxCents: z.number().int().nonnegative(),
  shippingCents: z.number().int().nonnegative(),
  totalCents: z.number().int().nonnegative(),
  // estado pós-pedido (as 3 ações)
  supplierResponse: z.enum(['pending', 'accepted', 'declined']),
  supplierRespondedAt: z.string().nullable(),
  supplierDeclinedReason: z.string().nullable(),
  supplierNfNumber: z.string().nullable(),
  supplierNfHasFile: z.boolean(),
  supplierNfAt: z.string().nullable(),
  supplierDeliveredAt: z.string().nullable(),
});
export type SupplierPortalPoDetail = z.infer<typeof supplierPortalPoDetailSchema>;

/** Fornecedor aceita ou recusa (com motivo) o pedido. */
export const supplierPoRespondRequestSchema = z
  .object({
    decision: z.enum(['accept', 'decline']),
    reason: z.string().trim().max(500).optional(),
  })
  .refine(
    (v) => v.decision === 'accept' || (v.reason != null && v.reason.length >= 2),
    { message: 'Informe o motivo da recusa', path: ['reason'] },
  );
export type SupplierPoRespondRequest = z.infer<
  typeof supplierPoRespondRequestSchema
>;

/** Fornecedor informa o número da nota fiscal (arquivo é multipart à parte). */
export const supplierPoNfRequestSchema = z.object({
  nfNumber: z.string().trim().min(1).max(80),
});
export type SupplierPoNfRequest = z.infer<typeof supplierPoNfRequestSchema>;

/** Fornecedor confirma que entregou (data opcional; default = agora). */
export const supplierPoDeliveredRequestSchema = z.object({
  deliveredAt: z.string().datetime({ offset: true }).optional(),
});
export type SupplierPoDeliveredRequest = z.infer<
  typeof supplierPoDeliveredRequestSchema
>;

/**
 * Payload completo do GET /v1/public/supplier/:token.
 */
export const supplierPortalViewSchema = z.object({
  supplier: z.object({
    id: z.string(),
    legalName: z.string(),
    displayName: z.string().nullable(),
    cnpj: z.string().nullable(),
    email: z.string().nullable(),
  }),
  generatedAt: z.string(),
  openQuotations: z.array(supplierPortalOpenQuotationSchema),
  activePos: z.array(supplierPortalActivePoSchema),
  recentPayments: z.array(supplierPortalPaymentSchema),
  totals: z.object({
    openQuotationCount: z.number().int().nonnegative(),
    activePoCount: z.number().int().nonnegative(),
    paid90dCents: z.number().int().nonnegative(),
  }),
});
export type SupplierPortalView = z.infer<typeof supplierPortalViewSchema>;
